import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Boxes, Package, TriangleAlert } from 'lucide-react'
import {
  Card, CardHeader, EmptyState, ErrorState, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, fetchStockLevels, fetchStockOnHand, formatCount, formatQuantity,
  nameIn, translateError,
  type StockLevelView, type StockOnHandView,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { AdjustmentsCard } from './AdjustmentsCard'

/* ============================================================================
   W-100 · LINEN AND CONSUMABLES.

   TWO PANELS, BECAUSE THERE ARE TWO QUESTIONS AND THEY ARE ASKED BY DIFFERENT PEOPLE
   AT DIFFERENT TIMES.

     "What do we own?"   — the manager's question, asked monthly. It is about LOSS:
                           four hundred towels last month, three hundred and sixty now.
     "What is on this
      floor right now?"  — the store keeper's question, asked every morning. It is
                           about REPLENISHMENT: which pantries are below par.

   A single table with a location filter would answer both badly. The manager would
   have to sum four pantries in their head, and the store keeper would have to filter
   before they could start.

   THE THIRD COLUMN IS THE ONE NOBODY ELSE HAS. "In rooms" is owned minus what is in a
   place we can count — the linen out on rails right now. It exists because a towel
   issued to a room is still the hotel's towel, and any system that forgets that
   reports the hotel buying ten thousand towels a year.
   ========================================================================= */

export function StockBoard({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const [location, setLocation] = useState<string | null>(null)

  const onHand = useQuery({
    queryKey: ['stock-on-hand', propertyId],
    queryFn: ({ signal }) => fetchStockOnHand(propertyId, { signal }),
    // Two minutes. Stock does not move like a room board does, and a store keeper
    // reading a shelf does not need the screen twitching at them.
    refetchInterval: 120_000,
    retry: false,
  })

  const levels = useQuery({
    queryKey: ['stock-levels', propertyId],
    queryFn: ({ signal }) => fetchStockLevels(propertyId, { signal }),
    refetchInterval: 120_000,
    retry: false,
  })

  /* The locations, derived from the rows drawn below rather than fetched separately.
     ONE NUMBER, ONE SOURCE applied to a filter: a location list from a second endpoint
     could offer a pantry that has no rows, and an empty screen with a filter applied
     reads as a bug rather than as an empty pantry. */
  const locations = useMemo(() => {
    const seen = new Map<string, string>()
    for (const row of levels.data ?? []) {
      if (!seen.has(row.stockLocationId))
        seen.set(row.stockLocationId, nameIn(locale, row.locationName, row.locationNameAr))
    }
    return [...seen.entries()]
  }, [levels.data, locale])

  const shown = useMemo(
    () => (levels.data ?? []).filter((r) => location === null || r.stockLocationId === location),
    [levels.data, location])

  const belowPar = useMemo(() => shown.filter((r) => r.isBelowPar), [shown])

  if (onHand.isLoading && !onHand.error) return <LoadingState rows={6} />

  if (onHand.error && !onHand.data) {
    if (onHand.error instanceof ApiError && onHand.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(onHand.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={onHand.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  return (
    <div className="flex flex-col gap-3.5">

      {/* **CQ-42 — CORRECTIONS WAITING FOR A SECOND PERSON.** Sprint 82, and first on the
          screen because it is the only thing here somebody else is waiting on. It draws
          nothing at all for a reader without `stock.adjust`. */}
      <AdjustmentsCard propertyId={propertyId} levels={levels.data ?? []} />

      {belowPar.length > 0 && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-hk border border-[#fcd34d] bg-[#fffbeb]
                     px-3 py-2 text-[12.5px] text-[#b45309]
                     dark:border-[#b45309] dark:bg-[#2a1e08] dark:text-[#fbbf24]"
        >
          <TriangleAlert className="size-4 shrink-0" />
          {/* Counted from the rows on screen, never sent alongside them. */}
          <span>{t.stockBelowPar.replace('{count}', formatCount(belowPar.length, locale))}</span>
        </div>
      )}

      <Card>
        <CardHeader
          title={t.stockOwnedTitle}
          subtitle={t.stockOwnedSubtitle}
          icon={<Boxes className="size-4" />}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                <th className="px-3 py-2 text-start font-medium">{t.stockItem}</th>
                <th className="px-3 py-2 text-end font-medium">{t.stockOwned}</th>
                <th className="px-3 py-2 text-end font-medium">{t.stockInLocations}</th>
                <th className="px-3 py-2 text-end font-medium">{t.stockInRooms}</th>
              </tr>
            </thead>
            <tbody>
              {(onHand.data ?? []).map((item) => (
                <OnHandRow key={item.stockItemId} item={item} locale={locale} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardHeader
          title={t.stockWhereTitle}
          icon={<Package className="size-4" />}
          actions={
            <div className="flex flex-wrap gap-1.5">
              <FilterChip
                label={t.stockAllLocations}
                active={location === null}
                onClick={() => setLocation(null)}
              />
              {locations.map(([id, name]) => (
                <FilterChip
                  key={id}
                  label={name}
                  active={location === id}
                  onClick={() => setLocation(id)}
                />
              ))}
            </div>
          }
        />
        <div className="overflow-x-auto">
          {shown.length === 0 ? (
            <EmptyState title={t.stockEmpty} />
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                  <th className="px-3 py-2 text-start font-medium">{t.stockItem}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.stockLocation}</th>
                  <th className="px-3 py-2 text-end font-medium">{t.stockQuantity}</th>
                  <th className="px-3 py-2 text-end font-medium">{t.stockPar}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <LevelRow
                    key={`${row.stockItemId}:${row.stockLocationId}`}
                    row={row}
                    locale={locale}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  )
}

function OnHandRow({ item, locale }: { item: StockOnHandView; locale: 'en' | 'ar' }) {
  const t = STRINGS[locale]
  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className="px-3 py-2">
        <bdi className="font-medium text-foreground">
          {nameIn(locale, item.itemName, item.itemNameAr)}
        </bdi>
        <span className="ms-2 text-[11px] text-muted-foreground">
          {item.kind === 'CIRCULATING' ? t.stockCirculating : t.stockConsumable}
        </span>
      </td>
      {/* `tnum` — tabular figures, so a column of numbers lines up. Eastern Arabic
          numerals in Arabic; the QUANTITY is a quantity, not an identifier. */}
      <td className="px-3 py-2 text-end tnum font-medium">{formatQuantity(item.totalOwned, locale)}</td>
      <td className="px-3 py-2 text-end tnum text-muted-foreground">
        {formatQuantity(item.inLocations, locale)}
      </td>
      <td className="px-3 py-2 text-end tnum">
        {/* Zero is shown as a dash, not as 0. For a consumable it is not "none out on
            rails", it is "the question does not apply" — and a column of zeroes invites
            somebody to add them up. */}
        {item.kind === 'CIRCULATING' && item.inRooms > 0
          ? <span className="text-primary">{formatQuantity(item.inRooms, locale)}</span>
          : <span className="text-muted-foreground">—</span>}
      </td>
    </tr>
  )
}

function LevelRow({ row, locale }: { row: StockLevelView; locale: 'en' | 'ar' }) {
  return (
    <tr className={`border-b border-border/60 last:border-0 ${
      row.isBelowPar ? 'bg-[#fffbeb] dark:bg-[#2a1e08]' : ''}`}
    >
      <td className="px-3 py-2">
        <bdi className="text-foreground">{nameIn(locale, row.itemName, row.itemNameAr)}</bdi>
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        <bdi>{nameIn(locale, row.locationName, row.locationNameAr)}</bdi>
      </td>
      <td className={`px-3 py-2 text-end tnum ${
        row.isBelowPar ? 'font-semibold text-[#b45309] dark:text-[#fbbf24]' : 'font-medium'}`}
      >
        {formatQuantity(row.quantity, locale)}
      </td>
      {/* **AN EM DASH WHERE NOBODY HAS SET A PAR** (W-104, Sprint 67), never a number.
          The laundry and the trolleys have none, and drawing a zero there would say the
          hotel wants them empty. */}
      <td className="px-3 py-2 text-end tnum text-muted-foreground">
        {row.parLevel === null ? '—' : formatQuantity(row.parLevel, locale)}
      </td>
    </tr>
  )
}

function FilterChip(
  { label, active, onClick }: { label: string; active: boolean; onClick: () => void },
) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-2.5 py-0.5 text-[11.5px] cursor-pointer ${
        active
          ? 'border-primary bg-primary/10 font-medium text-primary'
          : 'border-border text-muted-foreground hover:bg-muted'}`}
    >
      <bdi>{label}</bdi>
    </button>
  )
}
